import React, { useState, useEffect, useContext } from "react"
import { graphql, useStaticQuery } from "gatsby"
import tw from "twin.macro"
import Pulse from "react-reveal/Pulse"
import { useQueryParam, NumberParam } from "use-query-params"
import useScrollbarSize from "react-scrollbar-size"
import ToolTip from "../components/tooltip"
import { navigate } from "gatsby"
// component imports
import { myContext } from "../context/Provider"
import Layout from "../components/layout"
import Seo from "../components/seo"
import createModal from "../components/modals/createModal"
import Hero from "../components/hero"
import {
  Container,
  Button,
  PageHeading,
  FaqLink,
  Footnotes,
} from "../components/auvelity-ui"
import InternalCta from "../components/internal-cta"
import VerticalCardDeck from "../components/vertical-card-deck"

import iconFish from "../images/icons/fish.svg"
import iconBike from "../images/icons/bike.svg"
import iconBook from "../images/icons/book.svg"
import iconHug from "../images/icons/hug.svg"
import iconNotes from "../images/icons/notes.svg"

import woman from "../images/resources/woman.png"
import shapes02 from "../images/resources/shapes02.svg"

// image imports
import verticalCardIconFast from "../images/how-auvelity-may-help/icon-fast.svg"
import verticalCardIconYear from "../images/how-auvelity-may-help/icon-oneyear.svg"
import circleBlue from "../images/shapes/geo-circle-blue.svg"
import triangleYellowTL from "../images/shapes/geo-triangle-yellow-tl.svg"
import triangleBlueTL from "../images/shapes/geo-triangle-blue-tl.svg"
import squareBlue from "../images/shapes/geo-square-blue.svg"
import circleYellow from "../images/shapes/geo-circle-yellow.svg"
import squareYellowTL from "../images/shapes/geo-square-yellow-tl.svg"

import bottleMan from "../images/slides/bottle-man.png";
import sunshine from "../images/slides/sunshine.png";
import mddFrame from "../images/slides/mdd-frame.png"
import person from "../images/home/person01.png"
import person02 from "../images/home/person02.png"
import home from "../images/how-auvelity-may-help/home.svg"
import workSchool from "../images/how-auvelity-may-help/work-school.svg"
import social from "../images/how-auvelity-may-help/social.svg"
import iconSlowedSpeech from "../images/icons/slowed-speech.svg"
import iconWeight from "../images/icons/weight.svg"
import iconSuicide from "../images/icons/suicide.svg"
import iconBed from "../images/icons/bed.svg"
import iconAnhedonia from "../images/icons/anhedonia.svg"
import iconDepressed from "../images/icons/depressed.svg"
import iconFatigue from "../images/icons/fatigue.svg"
import iconWorthless from "../images/icons/worthless.svg"

import iconSocial from "../images/icons/social.svg"
import iconNumb from "../images/icons/numb.svg"
import iconDifficult from "../images/icons/food.svg"
import iconMotivation from "../images/icons/motivation.svg"

import MeccaImage from "../images/slides/mecca-no-quote.png";

const cardDataObj = [
  {
    colorVariant: "grey",
    icon: verticalCardIconFast,
    title: <>Started working for some people as early as&nbsp;1&nbsp;week</>,
    body: (
      <>
        On average, people taking AUVELITY had a rapid improvement in their
        depressive symptom scores vs placebo as early as&nbsp;1&nbsp;week.
      </>
    ),
  },
  {
    colorVariant: "yellow",
    icon: verticalCardIconYear,
    iconAlt: "1 year",
    title: (
      <>
        Reduced depression scores seen in a study lasting
        up&nbsp;to&nbsp;12&nbsp;months
      </>
    ),
    body: (
      <>
        Average depression scores were reduced in a study lasting up to 12
        months where both researchers and patients knew they were taking
        AUVELITY.
        <sup>&dagger;</sup>
      </>
    ),
  },
]
const icons = [home, workSchool, social]

const MeasurementData = () => (
  <div tw="lg:pl-10">
    <h3 tw="text-yellow font-bold leading-tight text-[1.65rem] mb-3 text-center md:(text-left text-3xl mb-5)">
      How did they measure improvement?
    </h3>
    <div tw="md:(flex justify-start)">
      <p tw="leading-tight md:(w-2/5 mr-16 leading-relaxed)">
        Symptom improvement was based on the overall change in a depression
        symptom scale that evaluated:
      </p>
      <ul tw="list-disc pl-5 mt-3 md:(w-2/5 mt-0)">
        <li tw="text-yellow md:(mb-1)">
          <span tw="text-white">Sadness</span>
        </li>
        <li tw="text-yellow md:(mb-1)">
          <span tw="text-white">Inner tension</span>
        </li>
        <li tw="text-yellow md:(mb-1)">
          <span tw="text-white">Reduced sleep</span>
        </li>
        <li tw="text-yellow md:(mb-1)">
          <span tw="text-white">Reduced appetite</span>
        </li>
        <li tw="text-yellow md:(mb-1)">
          <span tw="text-white">Difficulty concentrating</span>
        </li>
        <li tw="text-yellow md:(mb-1)">
          <span tw="text-white">Lack of interest</span>
        </li>
        <li tw="text-yellow md:(mb-1)">
          <span tw="text-white">Lack of energy/motivation</span>
        </li>
        <li tw="text-yellow md:(mb-1)">
          <span tw="text-white">Inability to feel</span>
        </li>
        <li tw="text-yellow md:(mb-1)">
          <span tw="text-white">Pessimism</span>
        </li>
        <li tw="text-yellow">
          <span tw="text-white">Thoughts of suicide</span>
        </li>
      </ul>
    </div>
  </div>
)

const AboutDepression = () => {
  const context = useContext(myContext)
  const { width } = useScrollbarSize()

  //show modal param
  const [showModal] = useQueryParam("showModal", NumberParam)

  useEffect(() => {
    if (showModal === 0) {
      createModal({
        scrollWidth: width,
        modalType: "definitions",
        modalChildren: <MeasurementData />,
      })
    }
  }, [showModal, width])

  const { bgdesktop, bgmobile, site } = useStaticQuery(graphql`
    query {
      bgdesktop: file(relativePath: { eq: "hero/hero-about-depression-desktop.jpg" }) {
        childImageSharp {
          gatsbyImageData(width: 1440, quality: 100, placeholder: BLURRED)
        }
      }
      bgmobile: file(
        relativePath: { eq: "hero/hero-about-depression-mobile.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 960, quality: 100, placeholder: BLURRED)
        }
      }
      site {
        siteMetadata {
          globalSchema
        }
      }
    }
  `)

  const heroData = {
    icon: ["triangleWhiteBR", "squareYellowTLCurved", "circleWhite"],
    title: "About Depression",
    subtitle: (
      <>
        What is major depressive disorder&nbsp;(MDD)?  
      </>
    ),
    fingerTabs: true,
    paragraph1: (
      <h2 style={{maxWidth: `400px`, fontSize: "18px"}}>
        Learn about some of the common symptoms of MDD.
      </h2>
    ),
    actorPortrayal: "white",
    classInfo: 'hero-about_depression'
  }

  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <Layout>
      <Seo
        title="What is Major Depressive Disorder (MDD)? - AUVELITY (dextromethorphan-bupropion)"
        description="Learn about common symptoms of MDD and find a treatment that's right for you. Please see Medication Guide, including Boxed Warning for suicidal thoughts and actions."
        socialTitle="What is Major Depressive Disorder (MDD)? - AUVELITY (dextromethorphan-bupropion)"
        socialDescription="Learn about common symptoms of MDD and find a treatment that's right for you. Please see Medication Guide, including Boxed Warning for suicidal thoughts and actions."
        schema={site.siteMetadata.globalSchema}
      />
      <Hero
        dataObj={heroData}
        env={"white"}
        bgImgData={!context.isMobile ? bgdesktop : bgmobile}
        variant="howitmayhelp"
      />

      <Container>
        <div tw="flex justify-start mt-10 mb-0 items-center flex-wrap md:(mt-24 mb-6)" className="mobile-reverse">
          <div tw="flex flex-col items-start justify-between mt-10 mb-10 lg:(w-1/2 leading-tight mt-0 mb-0)">
            <img src={person} alt="" tw="max-width[350px] self-center" />
            <p tw="text-center mt-5 max-width[350px] self-center text-blue sm:(w-full) lg:(text-left)">Actor portrayal.</p>
          </div>
          <div tw="lg:(w-1/2 leading-tight)">
            <div tw="flex justify-start">
              <img
                src={circleBlue}
                tw="mr-2 w-6 md:(w-auto)"
                alt="circle blue icon"
              />
              <img
                src={triangleYellowTL}
                tw="mr-2 w-6 md:(w-auto)"
                alt="triangle yellow icon"
              />
              <img
                src={squareBlue}
                alt="square blue icon"
              />
            </div>
            <h3 tw="text-2xl text-blue max-w-2xl font-bold tracking-tight leading-tight mt-3 md:(mt-5) lg:(text-4xl)">
              Here’s the deal
            </h3>
            <p tw="mt-5 max-width[505px] text-[20px] text-lightblack">
            MDD isn’t just a “bad day” or a “rough patch.” It is a common form of depression that can be severe. An estimated 21 million adults in the US have experienced a major depressive&nbsp;episode.
            </p>
            <p tw="mt-5 max-width[520px] text-[20px] text-lightblack">
              One of the first signs you may have MDD is that you feel down every day, for <strong tw="text-blue">at least 2 weeks</strong>, and the symptoms don't stop at&nbsp;sadness.
            </p>
          </div>
        </div>
      </Container>
      
      <div tw="bg-blue text-white mt-0 rounded-tr-regular rounded-bl-regular p-6 pb-20 pt-20 lg:(rounded-tl-regular rounded-br-regular rounded-tr-[0] rounded-bl-[0]  p-20 mt-14 relative)">
        <Container>
          <PageHeading color="white" center={true}>Typical symptoms of MDD may include:</PageHeading>
          <div tw="flex flex-col flex-wrap lg:(flex-row)">
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[100%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconWorthless} alt="Sad face icon" />
                </div>
                <div tw="w-full px-4 pt-8 pl-0 pr-0 text-sm text-center font-semibold lg:(px-0 pt-0 pr-4 text-left text-xl)">
                  Feeling worthless or guilty often
                </div>
              </div>
            </div>
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[100%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconSlowedSpeech} alt="Slowed speech icon" />
                </div>
                <div tw="w-full px-4 pt-8 pl-0 pr-0 text-sm text-center font-semibold lg:(px-0 pt-0 pr-4 text-left text-xl)">
                  Slowed speech
                </div>
              </div>
            </div>
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[100%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconWeight} alt="Weight icon" />
                </div>
                <div tw="w-full px-4 pt-8 pl-0 pr-0 text-sm text-center font-semibold lg:(px-0 pt-0 pr-4 text-left text-xl)">
                  Weight gain or weight loss, resulting from changes in your appetite
                </div>
              </div>
            </div>
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[100%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconSuicide} alt="Suicide icon" />
                </div>
                <div tw="w-full px-4 pt-8 pl-0 pr-0 text-sm text-center font-semibold lg:(px-0 pt-0 pr-4 text-left text-xl)">
                  Thoughts of death or suicide
                </div>
              </div>
            </div>
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[100%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconBed} alt="Bed icon" />
                </div>
                <div tw="w-full px-4 pt-8 pl-0 pr-0 text-sm text-center font-semibold lg:(px-0 pt-0 pr-4 text-left text-xl)">
                  Trouble sleeping or sleeping too much
                </div>
              </div>
            </div>
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[100%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconAnhedonia} alt="anhedonia icon" />
                </div>
                <div tw="w-full px-4 pt-8 pl-0 pr-0 text-sm text-center font-semibold lg:(px-0 pt-0 pr-4 text-left text-xl)">
                  Diminished interest in pleasurable activities (anhedonia)
                </div>
              </div>
            </div>
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[100%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconDepressed} alt="Bed icon" />
                </div>
                <div tw="w-full px-4 pt-8 pl-0 pr-0 text-sm text-center font-semibold lg:(px-0 pt-0 pr-4 text-left text-xl)">
                  Depressed mood
                </div>
              </div>
            </div>
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[100%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconFatigue} alt="Sun icon" />
                </div>
                <div tw="w-full px-4 pt-8 pl-0 pr-0 text-sm text-center font-semibold lg:(px-0 pt-0 pr-4 text-left text-xl)">
                  Tiredness, fatigue, low energy, or decreased movement; struggling to complete everyday&nbsp;tasks
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <Container>
        <div tw="flex rounded-tl-[100px] rounded-br-[100px] justify-center mt-24 mb-36 bg-yellow p-12 mb-0 items-center flex-wrap" className="mobile-reverse">
          <div tw="flex justify-center mb-0 mt-10 lg:(w-2/5 leading-tight mt-0 mb-0)">
            <img src={MeccaImage} alt="Mecca. Taking AUVELITY since 2023." width={363} />
          </div>
          <div tw="lg:(w-3/5 leading-tight pl-20)">
            <h3 tw="text-2xl text-center text-blue max-w-2xl font-bold tracking-tight leading-tight mt-3 md:(mt-5) lg:(text-4xl text-left)">
              You are not alone
            </h3>
            <p tw="mt-5 text-center max-width[400px] text-[22px] line-height[36px] lg:(text-left)">
              Hear inspirational stories from real people taking AUVELITY.
            </p>
            <p tw="mt-5 text-blue">
              <Button
                arrow
                onClick={() => navigate('/patient-stories')}
                variant="blue"
                env="yellow"
                addClassForGTM="get-to-know-them"
                className="get-to-know"
              >
                Get to know them
              </Button>
            </p>
          </div>
        </div>
      </Container>

      <Container>
        <div tw="flex justify-start mt-10 mb-0 items-center flex-wrap md:(mt-24 mb-36)" className="mobile-reverse">
          <div tw="flex flex-col items-end justify-between mb-10 lg:(w-2/5 leading-tight mb-0)">
            <img src={sunshine} tw="max-w-[330px] self-center" />
          </div>
          <div tw="mt-10 lg:(w-3/5 leading-tight pl-20 mt-0)">
            <div tw="flex justify-start">
              <img
                src={circleBlue}
                tw="mr-2 w-6 md:(w-auto)"
                alt="circle blue icon"
              />
              <img
                src={triangleYellowTL}
                tw="mr-2 w-6 md:(w-auto)"
                alt="triangle yellow icon"
              />
              <img
                src={squareBlue}
                alt="blue square icon"
              />
            </div>
            <h3 tw="text-2xl text-blue text-left max-w-2xl font-bold tracking-tight leading-tight mt-3 md:(mt-5) lg:(text-4xl text-left)">
              Ever heard of anhedonia (an&#8209;he&#8209;DOEN&#8209;ia)?
            </h3>
            <p tw="mt-5 max-width[500px] text-left text-[20px] lg:(text-left)">
              Anhedonia is a symptom of depression that makes it difficult to find interest or pleasure in activities you'd otherwise find enjoyable.
            </p>
            <p tw="mt-5 mb-5 text-blue max-width[500px] text-[20px] text-left lg:(text-left mb-0)">
              <strong>As many as 75% of people living with depression struggle with anhedonia.</strong>
            </p>
          </div>
        </div>
      </Container>
      <div tw="bg-blue text-white rounded-tr-regular rounded-bl-regular p-6 pb-20 lg:(rounded-tl-regular rounded-br-regular p-20 -mt-14 relative rounded-tr-[0] rounded-bl-[0])">
        <Container>
          <PageHeading color="white" center={true}>Common signs of anhedonia may include:</PageHeading>
          <div tw="flex flex-col flex-wrap lg:(flex-row)">
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[90%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconSocial} alt="Loss of interest icon" />
                </div>
                <div tw="w-full px-4 pt-8 pl-0 pr-0 text-sm text-center font-semibold lg:(px-0 pt-0 pr-4 text-left text-xl)">
                  Loss of interest in social activities or&nbsp;hobbies
                </div>
              </div>
            </div>
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[90%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconNumb} alt="Numb icon" />
                </div>
                <div tw="w-full px-4 pt-8 pl-0 pr-0 text-sm text-center font-semibold lg:(px-0 pt-0 pr-4 text-left text-xl)">
                  Feeling emotionally numb or&nbsp;disconnected
                </div>
              </div>
            </div>
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[90%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconDifficult} alt="Difficulty icon" />
                </div>
                <div tw="w-full px-4 pt-8 pl-0 pr-0 text-sm text-center font-semibold lg:(px-0 pt-0 pr-4 text-left text-xl)">
                  Lack of pleasure in eating, intimacy, or other activities you once enjoyed
                </div>
              </div>
            </div>
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[90%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconMotivation} alt="Motivation icon" />
                </div>
                <div tw="w-full px-4 pt-8 pl-0 pr-0 text-sm text-center font-semibold lg:(px-0 pt-0 pr-4 text-left text-xl)">
                  Difficulty finding motivation for things you used to care about
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <Container>
        <div css={[ tw`flex flex-col w-full mt-0 items-center text-center pt-16 pb-6 lg:(h-full text-left py-0 flex-row justify-between mt-0)` ]} >
          <div className="content-container" css={[ tw`w-full min-h-full flex flex-col justify-start items-center relative py-1 lg:(w-3/5 justify-center items-start px-1) `, ]} >
            <div tw="flex justify-start">
              <img src={circleYellow} tw="mr-2 w-6 md:(w-auto)" alt="circle yellow icon" />
              <img src={triangleBlueTL} tw="mr-2 w-6 md:(w-auto)" alt="triangle blue icon" />
              <img src={squareYellowTL} alt="yellow square icon" />
            </div>
            <h3 tw="text-2xl text-left w-full text-blue max-w-2xl font-bold tracking-tight leading-tight mt-3 md:(mt-5) lg:(text-4xl)">
              Finding a treatment that's right for&nbsp;you
            </h3>
            <p tw="mt-5 text-left w-full text-lightblack lg:(max-width[500px])">
              Living with depression can be challenging. MDD can look different for each person, and it’s important to know your treatment options so that you and your healthcare provider can find the best option for you.
            </p>
            <p tw="mt-5 text-blue">
              <Button arrow onClick={() => navigate('/how-auvelity-may-help')} >
                Learn how AUVELITY could help
              </Button>
            </p>
          </div>
          <div className="content-container" css={[ tw`w-full min-h-full flex flex-col justify-start items-center relative py-1 lg:(w-2/5 justify-center items-start px-1) `, ]} >
             <img src={person02} alt="" />
             <p tw="text-center mt-5 text-blue sm:(w-full) lg:(text-right)">Actor portrayal.</p>
          </div>
        </div>
      </Container>
      <section
        css={[
          tw`bg-blue py-24 pt-0 rounded-tr-[100px] text-white rounded-bl-[100px] lg:(pt-0)`,
        ]}
      >
        <Container>
          <div
            css={[
              tw`flex flex-col w-full items-center text-center pt-16 pb-6 lg:(h-full pt-16 text-left py-0 flex-row justify-between)`
            ]}
          >
            <div
              className="content-container"
              css={[
                tw`w-full min-h-full flex flex-col justify-start items-center relative py-1 lg:(w-2/5 justify-center items-start px-1) `,
              ]}
            >
              <img src={shapes02} css={[tw`width[5rem] mb-2`]} alt="Shape icon" />
              <h2
                css={[
                  tw`text-2xl text-left text-yellow font-bold leading-tight mb-1 lg:(mt-0 text-4xl mb-4 text-left)`,
                ]}
              >
                Everyday habits that may help ease depression
              </h2>
              <p tw="text-left">
                Depression will try to keep you down. Help manage your symptoms with these lifestyle tips. 
              </p>
            </div>
            <div
              className="content-container"
              css={[
                tw`w-full min-h-full flex flex-col justify-start items-center relative py-1 lg:(w-2/5 justify-center items-start px-1 mt-10) `,
              ]}
            >
              <img src={woman} alt="" />
              <div tw="text-white text-right text-xs lg:(text-ms) absolute right-[50px] bottom-[-24px]">
                Actor portrayal.
              </div>
            </div>
          </div>
          <div tw="flex flex-col flex-wrap lg:(flex-row)">
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[90%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconFish} alt="Fish icon" />
                </div>
                <div tw="w-full px-4 pt-8">
                  <h2 css={['font-size: 28px; font-weight:700;']}>Eat a healthy diet</h2>
                  <p tw="mt-5">Eating well and staying hydrated can have a positive impact on your mental health. Try taking in foods rich in omega-3 fatty acids like fish, nuts, and seeds, all of which are mood&nbsp;boosters.</p>
                </div>
              </div>
            </div>
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[90%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconBike} alt="Bike icon" />
                </div>
                <div tw="w-full px-4 pt-8">
                  <h2 css={['font-size: 28px; font-weight:700;']}>Stay on the move</h2>
                  <p tw="mt-5">When low energy meets low motivation, even the idea of exercise can seem daunting. Start with small movement goals, like a walk around the block, a gentle stretch, or even a few minutes of dancing in your living&nbsp;room. Just be sure to get the "OK" from your healthcare provider first.</p>
                </div>
              </div>
            </div>
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[90%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconHug} alt="Hugging icon" />
                </div>
                <div tw="w-full px-4 pt-8">
                  <h2 css={['font-size: 28px; font-weight:700;']}>Keep on connecting</h2>
                  <p tw="mt-5">Maintaining social connections can be a challenge with MDD, but even small interactions can combat feelings of isolation. Start by reaching out to a friend, family member, or support&nbsp;group.</p>
                </div>
              </div>
            </div>
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[90%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconNotes} alt="Notes icon" />
                </div>
                <div tw="w-full px-4 pt-8">
                  <h2 css={['font-size: 28px; font-weight:700;']}>Set up a daily routine</h2>
                  <p tw="mt-5">A lack of structure can sometimes make things feel hopeless, especially in MDD. Try to create a basic daily routine, even if it's as simple as “wake up, get dressed, eat&nbsp;breakfast.”</p>
                </div>
              </div>
            </div>
            <div tw="w-full lg:w-1/2">
              <div tw="flex flex-col items-center mt-20 lg:(flex-row justify-between mt-12 w-[90%])">
                <div tw="w-44 lg:(mr-8)">
                  <img tw="w-full" src={iconBook} alt="Book icon" />
                </div>
                <div tw="w-full px-4 pt-8">
                  <h2 css={['font-size: 28px; font-weight:700;']}>Get creative</h2>
                  <p tw="mt-5">Creative activities can act as outlets for expression and give a sense of purpose—even on low-energy days. This might be journaling, painting, writing, or&nbsp;cooking.</p>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </Layout>
  )
}

export default AboutDepression
